// Aba "Mês" — equivale às abas mensais da planilha: projeto com seus
// entregáveis, previsão (data + probabilidade), entregar/medir e as
// fotografias do baseline (início do mês) e do ajuste (dia 15).
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  App as AntApp, Button, Card, Col, DatePicker, Empty, Flex, Input, InputNumber, Modal, Row, Select, Spin,
  Statistic, Switch, Table, Tag, Tooltip, Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import * as api from './api';
import type { Item, LinhaContrato, Mes } from './api';
import { brl, dataBr, nomeDe, pct, PROBABILIDADES, rotuloCompetencia, textoRateio } from './regras';

const { Text } = Typography;
const MODAL = { width: '94vw' as const, style: { maxWidth: 760, top: 16 }, styles: { body: { maxHeight: 'calc(100vh - 130px)', overflowY: 'auto' as const } } };

const estiloItem = (i: Item): React.CSSProperties =>
  i.situacao === 'entregue' ? { color: '#2f7d4f' }
    : i.situacao === 'cancelado' ? { color: '#9aa0a6', textDecoration: 'line-through' }
      : i.situacao === 'atrasado' ? { color: '#c0392b' } : {};
const TAG_SITUACAO: Record<string, { cor: string; texto: string }> = {
  entregue: { cor: 'green', texto: 'Entregue' }, pendente: { cor: 'blue', texto: 'A entregar' },
  atrasado: { cor: 'red', texto: 'Atrasado' }, cancelado: { cor: 'default', texto: 'Cancelado' },
};

export default function AbaMes() {
  const { message, modal } = AntApp.useApp();
  const [comp, setComp] = useState<Dayjs>(dayjs().startOf('month'));
  const [dados, setDados] = useState<Mes | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [mostrarConcluidos, setMostrarConcluidos] = useState(false);
  const [busca, setBusca] = useState('');
  const [itemEditando, setItemEditando] = useState<{ item: Partial<Item>; contrato: LinhaContrato | null } | null>(null);
  const [entregando, setEntregando] = useState<Item | null>(null);
  const [historicoDe, setHistoricoDe] = useState<Item | null>(null);
  const [adicionando, setAdicionando] = useState(false);
  const [abertura, setAbertura] = useState<LinhaContrato | null>(null);

  const competencia = comp.format('YYYY-MM');
  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setDados(await api.obterMes(competencia)); }
    catch (e) { message.error(`Não carregou o mês: ${api.erroDe(e)}`); }
    finally { setCarregando(false); }
  }, [competencia, message]);
  useEffect(() => { carregar(); }, [carregar]);

  const podeEditar = !!dados?.pode_editar;
  const mesControlado = !!dados && `${competencia}-01` >= dados.inicio_controle;

  async function salvarCampo(i: Item, campos: Record<string, any>) {
    try { await api.salvarItem({ id: i.id, versao: i.versao, ...campos }); await carregar(); }
    catch (e) { message.error(api.erroDe(e)); await carregar(); }
  }

  async function fotografar(tipo: 'baseline' | 'ajuste', sobrescrever = false) {
    try {
      const r = await api.fotografar(competencia, tipo, sobrescrever);
      message.success(`${tipo === 'baseline' ? 'Baseline' : 'Ajuste'} de ${rotuloCompetencia(`${competencia}-01`)} salvo: ${brl(r.total_provavel)} prováveis em ${r.itens.length} entregáveis.`);
      await carregar();
    } catch (e) {
      if (api.jaExiste(e)) {
        modal.confirm({
          title: 'A previsão mensal já foi efetuada',
          content: `${api.erroDe(e)} A versão anterior fica guardada no histórico, mas deixa de ser a referência dos indicadores.`,
          okText: 'Sobrescrever', okButtonProps: { danger: true }, cancelText: 'Manter a atual',
          onOk: () => fotografar(tipo, true),
        });
      } else message.error(api.erroDe(e));
    }
  }

  const linhas = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return (dados?.linhas || []).filter(l =>
      (mostrarConcluidos || l.saldo > 0.005 || l.entregue_mes > 0 || l.itens.some(i => i.status === 'pendente'))
      && (!t || `${l.numero} ${l.titulo} ${l.responsavel || ''}`.toLowerCase().includes(t)));
  }, [dados, mostrarConcluidos, busca]);

  const meta = dados?.meta?.valor ?? null;
  const entregue = dados?.totais.entregue_mes ?? 0;

  const colunasItens = (l: LinhaContrato): ColumnsType<Item> => [
    {
      title: 'Entregável', dataIndex: 'descricao', render: (v, i) => (
        <span style={estiloItem(i)}>
          {i.situacao === 'entregue' ? '✓ ' : ''}{v}
          {i.tipo === 'quantidade' && <Text type="secondary"> · {i.entregue_quantidade}/{i.quantidade_total} {i.unidade}</Text>}
          {i.antes_do_controle && <Tag style={{ marginLeft: 6 }}>antes do controle</Tag>}
        </span>
      ),
    },
    { title: 'Valor', dataIndex: 'valor', align: 'right', width: 110, render: v => brl(v) },
    { title: 'Saldo', dataIndex: 'saldo', align: 'right', width: 110, render: v => brl(v) },
    {
      title: 'Previsão', width: 140, render: (_, i) => i.status !== 'pendente'
        ? <Text type="secondary">{i.entregue_em ? `entregue ${dataBr(i.entregue_em)}` : '—'}</Text>
        : <DatePicker size="small" format="DD/MM/YYYY" disabled={!podeEditar} value={i.data_prevista ? dayjs(i.data_prevista) : null}
          onChange={d => salvarCampo(i, { data_prevista: d ? d.format('YYYY-MM-DD') : null })} />,
    },
    {
      title: 'Probabilidade', width: 140, render: (_, i) => i.status !== 'pendente' ? null : (
        <Select size="small" style={{ width: 128 }} allowClear disabled={!podeEditar} placeholder="—"
          showSearch optionFilterProp="label" options={PROBABILIDADES} value={i.probabilidade ?? undefined}
          onChange={v => salvarCampo(i, { probabilidade: v ?? null })} />
      ),
    },
    {
      title: <Tooltip title="Quanto pode ser entregue neste mês. Vazio = o saldo do item.">Possível no mês</Tooltip>, width: 140, align: 'right',
      render: (_, i) => i.status !== 'pendente' ? null : i.tipo === 'quantidade'
        ? <InputNumber key={`${i.id}-${i.versao}`} size="small" min={0} disabled={!podeEditar} style={{ width: 120 }} placeholder={`qtd (${i.unidade || 'un'})`}
          defaultValue={i.previsto_quantidade ?? undefined} decimalSeparator=","
          onBlur={e => { const v = e.target.value === '' ? null : Number(String(e.target.value).replace(',', '.')); if (v !== i.previsto_quantidade) salvarCampo(i, { previsto_quantidade: v }); }} />
        : <InputNumber key={`${i.id}-${i.versao}`} size="small" min={0} disabled={!podeEditar} style={{ width: 120 }} placeholder={brl(i.saldo)}
          defaultValue={i.previsto_valor ?? undefined} decimalSeparator=","
          onBlur={e => { const v = e.target.value === '' ? null : Number(String(e.target.value).replace(/\./g, '').replace(',', '.')); if (v !== i.previsto_valor) salvarCampo(i, { previsto_valor: v }); }} />,
    },
    { title: 'Provável', dataIndex: 'provavel', align: 'right', width: 110, render: (v, i) => i.na_previsao ? brl(v) : <Text type="secondary">—</Text> },
    { title: 'Situação', dataIndex: 'situacao', width: 100, render: v => <Tag color={TAG_SITUACAO[v]?.cor}>{TAG_SITUACAO[v]?.texto || v}</Tag> },
    {
      title: '', width: 220, render: (_, i) => (
        <Flex gap={4} wrap>
          {podeEditar && i.status === 'pendente' && <Button size="small" type="primary" onClick={() => setEntregando(i)}>{i.tipo === 'quantidade' ? 'Medir' : 'Entregar'}</Button>}
          {podeEditar && <Button size="small" onClick={() => setItemEditando({ item: i, contrato: l })}>Editar</Button>}
          <Button size="small" type="link" onClick={() => setHistoricoDe(i)}>Lançamentos</Button>
        </Flex>
      ),
    },
  ];

  const colunas: ColumnsType<LinhaContrato> = [
    {
      title: 'Projeto', dataIndex: 'numero', fixed: 'left', width: 260, render: (_, l) => (
        <div>
          <Text strong>{l.numero}</Text> <Text>{l.titulo}</Text>
          <div><Text type="secondary" style={{ fontSize: 12 }}>{textoRateio(l.itens[0]?.rateio) || l.setor || 'sem setor'} · {nomeDe(l.responsavel)}</Text></div>
        </div>
      ),
    },
    { title: 'Valor total', dataIndex: 'valor_total', align: 'right', render: v => brl(v) },
    { title: 'Entregue acumulado', dataIndex: 'acumulado', align: 'right', render: v => brl(v) },
    { title: 'Saldo', dataIndex: 'saldo', align: 'right', render: v => brl(v) },
    { title: 'Possível no mês', dataIndex: 'possivel', align: 'right', render: v => brl(v) },
    { title: 'Prob.', dataIndex: 'probabilidade_media', align: 'right', render: v => (v == null ? '—' : `${v}%`) },
    { title: 'Provável', dataIndex: 'provavel', align: 'right', render: v => <Text strong>{brl(v)}</Text> },
    { title: 'Entregue no mês', dataIndex: 'entregue_mes', align: 'right', render: v => <Text style={{ color: v > 0 ? '#2f7d4f' : undefined }} strong={v > 0}>{brl(v)}</Text> },
    {
      title: 'Saldo a entregar', dataIndex: 'saldo_a_entregar', align: 'right', render: (v, l) => (
        <span>{brl(v)}{Math.abs(l.diferenca_itens) > 1 && (
          <Tooltip title={`Os entregáveis em aberto não fecham com o saldo do contrato (diferença de ${brl(l.diferenca_itens)}). Confira parcelas, aditivos ou o acumulado de abertura.`}>
            <Tag color="orange" style={{ marginLeft: 6 }}>≠ itens</Tag>
          </Tooltip>)}
        </span>
      ),
    },
    {
      title: '', width: 120, render: (_, l) => podeEditar && (
        <Select size="small" placeholder="Ações" style={{ width: 110 }} value={null as any}
          options={[
            { value: 'novo', label: 'Novo entregável' }, { value: 'puxar', label: 'Puxar parcelas' },
            { value: 'abertura', label: 'Acumulado de abertura' },
            { value: 'retirar', label: 'Retirar do controle' },
          ]}
          onChange={async v => {
            if (v === 'retirar') modal.confirm({
              title: `Retirar ${l.numero} do controle de entregas?`,
              content: 'Apaga os entregáveis, as entregas e o acumulado deste contrato no controle (o registro de auditoria guarda o resumo). Contratos e parcelas não mudam. Só depois disso o contrato pode ser excluído.',
              okText: 'Retirar', okButtonProps: { danger: true },
              onOk: async () => {
                try { const r = await api.removerDoControle(l.contrato_id); message.success(`${l.numero} retirado: ${r.itens} entregável(is) e ${r.lancamentos} lançamento(s).`); await carregar(); }
                catch (e) { message.error(api.erroDe(e)); }
              },
            });
            if (v === 'novo') setItemEditando({ item: { contrato_id: l.contrato_id, tipo: 'valor' }, contrato: l });
            if (v === 'abertura') setAbertura(l);
            if (v === 'puxar') {
              try { const r = await api.puxarParcelas(l.contrato_id); message.success(`${r.criados} entregável(is) criado(s) a partir das parcelas${r.antes_do_controle ? ` (${r.antes_do_controle} já faturado(s) antes do controle)` : ''}.`); await carregar(); }
              catch (e) { message.error(api.erroDe(e)); }
            }
          }} />
      ),
    },
  ];

  return (
    <div>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 12 }}>
        <DatePicker picker="month" format="MMMM/YYYY" allowClear={false} value={comp} onChange={d => d && setComp(d.startOf('month'))} />
        <Input.Search placeholder="Buscar projeto ou responsável" allowClear style={{ width: 260 }} onChange={e => setBusca(e.target.value)} />
        <Flex gap={6} align="center"><Switch size="small" checked={mostrarConcluidos} onChange={setMostrarConcluidos} /><Text>Mostrar concluídos</Text></Flex>
        <div style={{ flex: 1 }} />
        {podeEditar && <Button onClick={() => setAdicionando(true)}>+ Contrato no controle</Button>}
        {podeEditar && mesControlado && <Button type="primary" onClick={() => fotografar('baseline')}>Salvar baseline do mês</Button>}
        {podeEditar && mesControlado && <Button onClick={() => fotografar('ajuste')}>Salvar ajuste (dia 15)</Button>}
      </Flex>
      {dados && !mesControlado && (
        <Card size="small" style={{ marginBottom: 12, background: '#fffbe6' }}>
          Meses antes de {rotuloCompetencia(dados.inicio_controle)} vêm do histórico da planilha (aba Metas e histórico). Aqui aparecem só os entregáveis e lançamentos do sistema.
        </Card>
      )}
      <Spin spinning={carregando}>
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
          <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Meta do mês" value={brl(meta)} /></Card></Col>
          <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title={<Tooltip title={dados?.baseline ? `Salvo por ${nomeDe(dados.baseline.salvo_por)} em ${dataBr(dados.baseline.salvo_em)}` : 'Ainda não salvo'}>(a) Baseline</Tooltip>} value={brl(dados?.baseline?.total_provavel)} /></Card></Col>
          <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title={<Tooltip title={dados?.ajuste ? `Salvo por ${nomeDe(dados.ajuste.salvo_por)} em ${dataBr(dados.ajuste.salvo_em)}` : 'Ainda não salvo'}>(b) Ajuste</Tooltip>} value={brl(dados?.ajuste?.total_provavel)} /></Card></Col>
          <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Provável agora" value={brl(dados?.totais.provavel)} /></Card></Col>
          <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="(d) Entregue no mês" value={brl(entregue)} valueStyle={{ color: '#2f7d4f' }} /></Card></Col>
          <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="% da meta · falta" value={meta ? pct((entregue / meta) * 100) : '—'} suffix={meta ? <Text type="secondary" style={{ fontSize: 13 }}> · {brl(Math.max(0, meta - entregue))}</Text> : null} /></Card></Col>
        </Row>
        {linhas.length ? (
          <Table<LinhaContrato>
            rowKey="contrato_id" size="small" dataSource={linhas} columns={colunas} pagination={false} scroll={{ x: 1400 }}
            expandable={{
              expandedRowRender: l => l.itens.length
                ? <Table<Item> rowKey="id" size="small" dataSource={l.itens} columns={colunasItens(l)} pagination={false} scroll={{ x: 1200 }} />
                : <Empty description="Sem entregáveis. Use Ações → Puxar parcelas ou Novo entregável." />,
            }}
            summary={() => dados && (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} /><Table.Summary.Cell index={1}><Text strong>TOTAL</Text></Table.Summary.Cell>
                  {(['valor_total', 'acumulado', 'saldo', 'possivel'] as const).map((k, i) => <Table.Summary.Cell key={k} index={2 + i} align="right"><Text strong>{brl(dados.totais[k])}</Text></Table.Summary.Cell>)}
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} align="right"><Text strong>{brl(dados.totais.provavel)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right"><Text strong>{brl(dados.totais.entregue_mes)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right"><Text strong>{brl(dados.totais.saldo_a_entregar)}</Text></Table.Summary.Cell>
                  <Table.Summary.Cell index={10} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        ) : !carregando && <Empty description="Nenhum contrato no controle ainda. Use “+ Contrato no controle” ou a aba Conciliação." />}
      </Spin>

      {itemEditando && <ModalItem estado={itemEditando} onFechar={() => setItemEditando(null)} onSalvo={carregar} />}
      {entregando && <ModalEntregar item={entregando} hoje={dados?.hoje || dayjs().format('YYYY-MM-DD')} onFechar={() => setEntregando(null)} onSalvo={carregar} />}
      {historicoDe && <ModalLancamentos item={historicoDe} podeEditar={podeEditar} onFechar={() => setHistoricoDe(null)} onMudou={carregar} />}
      {adicionando && <ModalAdicionarContrato onFechar={() => setAdicionando(false)} onSalvo={carregar} />}
      {abertura && <ModalAbertura linha={abertura} inicio={dados?.inicio_controle || ''} onFechar={() => setAbertura(null)} onSalvo={carregar} />}
    </div>
  );
}

function ModalItem({ estado, onFechar, onSalvo }: { estado: { item: Partial<Item>; contrato: LinhaContrato | null }; onFechar: () => void; onSalvo: () => void }) {
  const { message, modal } = AntApp.useApp();
  const [f, setF] = useState<Partial<Item>>({ tipo: 'valor', ...estado.item });
  const novo = !estado.item.id;
  const set = (k: keyof Item, v: any) => setF(p => ({ ...p, [k]: v }));
  async function salvar() {
    try {
      await api.salvarItem({
        id: f.id, versao: f.versao, contrato_id: f.contrato_id, descricao: f.descricao, tipo: f.tipo, valor: f.valor,
        quantidade_total: f.tipo === 'quantidade' ? f.quantidade_total : undefined, unidade: f.tipo === 'quantidade' ? f.unidade : undefined,
        data_prevista: f.data_prevista ?? null, probabilidade: f.probabilidade ?? null, observacao: f.observacao ?? null,
      });
      message.success(novo ? 'Entregável criado.' : 'Entregável salvo.');
      onSalvo(); onFechar();
    } catch (e) { message.error(api.erroDe(e)); }
  }
  async function cancelarOuExcluir(acao: 'cancelar' | 'reabrir' | 'excluir') {
    try {
      if (acao === 'excluir') await api.excluirItem(f.id!);
      else await api.salvarItem({ id: f.id, versao: f.versao, status: acao === 'cancelar' ? 'cancelado' : 'pendente' });
      onSalvo(); onFechar();
    } catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title={novo ? `Novo entregável — ${estado.contrato?.numero || ''}` : 'Editar entregável'} onCancel={onFechar} onOk={salvar} okText="Salvar" destroyOnHidden {...MODAL}
      footer={(_, { OkBtn, CancelBtn }) => (
        <Flex justify="space-between" wrap gap={8}>
          <Flex gap={8}>
            {!novo && f.status === 'pendente' && <Button danger onClick={() => modal.confirm({ title: 'Cancelar este entregável?', content: 'Ele sai da previsão e do saldo em aberto; o registro fica.', onOk: () => cancelarOuExcluir('cancelar') })}>Cancelar entregável</Button>}
            {!novo && f.status === 'cancelado' && <Button onClick={() => cancelarOuExcluir('reabrir')}>Reabrir</Button>}
            {!novo && <Button type="text" danger onClick={() => modal.confirm({ title: 'Excluir de vez?', content: 'Só é possível sem nenhuma entrega lançada.', onOk: () => cancelarOuExcluir('excluir') })}>Excluir</Button>}
          </Flex>
          <Flex gap={8}><CancelBtn /><OkBtn /></Flex>
        </Flex>
      )}>
      <Flex vertical gap={10}>
        <div><Text>Descrição</Text><Input value={f.descricao} maxLength={300} onChange={e => set('descricao', e.target.value)} placeholder="Ex.: Relatório 1 de modelagem" /></div>
        <Flex gap={12} wrap>
          <div><Text>Medição</Text><br />
            <Select style={{ width: 210 }} value={f.tipo} disabled={!novo && (f.entregue_valor || 0) > 0} showSearch optionFilterProp="label"
              options={[{ value: 'valor', label: 'Por valor (produto/etapa)' }, { value: 'quantidade', label: 'Por quantidade (km, un…)' }]}
              onChange={v => set('tipo', v)} /></div>
          <div><Text>Valor total (R$)</Text><br /><InputNumber style={{ width: 160 }} min={0} decimalSeparator="," value={f.valor} onChange={v => set('valor', v)} /></div>
          {f.tipo === 'quantidade' && <>
            <div><Text>Quantidade contratada</Text><br /><InputNumber style={{ width: 140 }} min={0} decimalSeparator="," value={f.quantidade_total ?? undefined} onChange={v => set('quantidade_total', v)} /></div>
            <div><Text>Unidade</Text><br /><Input style={{ width: 90 }} maxLength={20} value={f.unidade ?? ''} placeholder="km" onChange={e => set('unidade', e.target.value)} /></div>
          </>}
        </Flex>
        <Flex gap={12} wrap>
          <div><Text>Data prevista</Text><br /><DatePicker format="DD/MM/YYYY" value={f.data_prevista ? dayjs(f.data_prevista) : null} onChange={d => set('data_prevista', d ? d.format('YYYY-MM-DD') : null)} /></div>
          <div><Text>Probabilidade</Text><br /><Select style={{ width: 160 }} allowClear showSearch optionFilterProp="label" options={PROBABILIDADES} value={f.probabilidade ?? undefined} onChange={v => set('probabilidade', v ?? null)} /></div>
        </Flex>
        <div><Text>Observação</Text><Input.TextArea rows={2} value={f.observacao ?? ''} onChange={e => set('observacao', e.target.value)} /></div>
        {f.data_prevista_original && <Text type="secondary">Prazo original (primeiro baseline): {dataBr(f.data_prevista_original)}</Text>}
      </Flex>
    </Modal>
  );
}

function ModalEntregar({ item, hoje, onFechar, onSalvo }: { item: Item; hoje: string; onFechar: () => void; onSalvo: () => void }) {
  const { message } = AntApp.useApp();
  const [data, setData] = useState<Dayjs>(dayjs(hoje));
  const [valor, setValor] = useState<number | null>(null);
  const [qtd, setQtd] = useState<number | null>(null);
  const [obs, setObs] = useState('');
  const porQtd = item.tipo === 'quantidade';
  const restanteQtd = (item.quantidade_total || 0) - (item.entregue_quantidade || 0);
  async function confirmar() {
    try {
      const r = await api.entregar({ item_id: item.id, data: data.format('YYYY-MM-DD'), valor: porQtd ? undefined : valor, quantidade: porQtd ? (qtd ?? 0) : undefined, observacao: obs || undefined });
      message.success(`${porQtd ? 'Medição' : 'Entrega'} de ${brl(r.valor, 2)} registrada${r.item_entregue ? ' — entregável concluído' : ''}. Isto não libera faturamento.`);
      onSalvo(); onFechar();
    } catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title={`${porQtd ? 'Medir' : 'Entregar'}: ${item.descricao}`} onCancel={onFechar} onOk={confirmar} okText={porQtd ? 'Registrar medição' : 'Registrar entrega'} destroyOnHidden {...MODAL}>
      <Flex vertical gap={10}>
        <Text type="secondary">Saldo do entregável: {brl(item.saldo, 2)}{porQtd ? ` · faltam ${restanteQtd} ${item.unidade || ''}` : ''}. Registrar a entrega não libera a parcela para faturar: isso continua no financeiro.</Text>
        <Flex gap={12} wrap>
          <div><Text>Data da entrega</Text><br /><DatePicker format="DD/MM/YYYY" allowClear={false} value={data} disabledDate={d => d.isAfter(dayjs(hoje), 'day')} onChange={d => d && setData(d)} /></div>
          {porQtd
            ? <div><Text>Quantidade medida ({item.unidade || 'un'})</Text><br /><InputNumber style={{ width: 160 }} min={0} max={restanteQtd} decimalSeparator="," value={qtd ?? undefined} onChange={v => setQtd(v)} />
              {qtd ? <Text type="secondary"> ≈ {brl(qtd * (item.valor / (item.quantidade_total || 1)), 2)}</Text> : null}</div>
            : <div><Text>Valor entregue (vazio = saldo inteiro)</Text><br /><InputNumber style={{ width: 180 }} min={0} max={item.saldo} decimalSeparator="," placeholder={brl(item.saldo, 2)} value={valor ?? undefined} onChange={v => setValor(v)} /></div>}
        </Flex>
        <div><Text>Observação</Text><Input.TextArea rows={2} value={obs} onChange={e => setObs(e.target.value)} /></div>
      </Flex>
    </Modal>
  );
}

function ModalLancamentos({ item, podeEditar, onFechar, onMudou }: { item: Item; podeEditar: boolean; onFechar: () => void; onMudou: () => void }) {
  const { message, modal } = AntApp.useApp();
  const [lista, setLista] = useState<api.Lancamento[] | null>(null);
  const carregar = useCallback(async () => {
    try { setLista((await api.lancamentosDoItem(item.id)).lancamentos); } catch (e) { message.error(api.erroDe(e)); }
  }, [item.id, message]);
  useEffect(() => { carregar(); }, [carregar]);
  return (
    <Modal open title={`Lançamentos — ${item.descricao}`} onCancel={onFechar} footer={null} destroyOnHidden {...MODAL}>
      <Table<api.Lancamento> rowKey="id" size="small" loading={!lista} dataSource={lista || []} pagination={false}
        rowClassName={l => (l.estornado_em ? 'em-estornado' : '')}
        columns={[
          { title: 'Data', dataIndex: 'data', render: (v, l) => <span style={l.estornado_em ? { textDecoration: 'line-through', color: '#9aa0a6' } : {}}>{dataBr(v)}</span> },
          { title: 'Tipo', dataIndex: 'origem' },
          { title: 'Valor', dataIndex: 'valor', align: 'right', render: v => brl(v, 2) },
          { title: 'Qtd', dataIndex: 'quantidade', align: 'right', render: v => (v == null ? '—' : v) },
          { title: 'Por', dataIndex: 'criado_por', render: v => nomeDe(v) },
          {
            title: '', render: (_, l) => l.estornado_em ? <Text type="secondary">estornado por {nomeDe(l.estornado_por)}</Text>
              : podeEditar && <Button size="small" danger onClick={() => modal.confirm({
                title: 'Estornar este lançamento?', content: 'O valor sai do realizado do mês e o entregável volta a pendente.',
                onOk: async () => { try { await api.estornar(l.id); await carregar(); onMudou(); } catch (e) { message.error(api.erroDe(e)); } },
              })}>Estornar</Button>,
          },
        ]} />
    </Modal>
  );
}

function ModalAdicionarContrato({ onFechar, onSalvo }: { onFechar: () => void; onSalvo: () => void }) {
  const { message } = AntApp.useApp();
  const [opcoes, setOpcoes] = useState<{ value: string; label: string }[]>([]);
  const [contrato, setContrato] = useState<string | null>(null);
  const [parcelas, setParcelas] = useState<api.ParcelaContrato[] | null>(null);
  useEffect(() => {
    api.buscarContratos('').then(r => setOpcoes(r.contratos.map(c => ({ value: c.id, label: `${c.numero} — ${c.titulo} (${brl(c.valor_total)})` }))))
      .catch(e => message.error(api.erroDe(e)));
  }, [message]);
  useEffect(() => {
    setParcelas(null);
    if (contrato) api.parcelasDoContrato(contrato).then(r => setParcelas(r.parcelas)).catch(e => message.error(api.erroDe(e)));
  }, [contrato, message]);
  async function puxar() {
    if (!contrato) return;
    try {
      const r = await api.puxarParcelas(contrato);
      message.success(`${r.criados} entregável(is) criado(s)${r.antes_do_controle ? `, ${r.antes_do_controle} já faturado(s) antes do controle` : ''}.`);
      onSalvo(); onFechar();
    } catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title="Contrato no controle de entregas" onCancel={onFechar} onOk={puxar} okText="Criar entregáveis das parcelas" okButtonProps={{ disabled: !contrato }} destroyOnHidden {...MODAL} style={{ ...MODAL.style, maxWidth: 980 }}>
      <Flex vertical gap={10}>
        <Select showSearch optionFilterProp="label" placeholder="Escolha o contrato (HY…)" options={opcoes} value={contrato ?? undefined} onChange={setContrato} style={{ width: '100%' }} />
        <Text type="secondary">Cada parcela do financeiro (contrato + aditivos) vira um entregável com o mesmo valor e a mesma divisão por setor. Parcela já faturada antes de outubro/2026 entra como “entregue antes do controle”. Nada muda no financeiro. Contrato medido por quantidade: crie o entregável à mão (Ações → Novo entregável).</Text>
        {contrato && (
          <Table<api.ParcelaContrato> rowKey="id" size="small" loading={!parcelas} dataSource={parcelas || []} pagination={false}
            columns={[
              { title: 'Parcela', render: (_, p) => `${p.numero_parcela ?? '—'}${p.total_parcelas ? '/' + p.total_parcelas : ''} ${p.titulo || ''}` },
              { title: 'Contrato', dataIndex: 'contrato_numero' },
              { title: 'Valor', dataIndex: 'valor', align: 'right', render: v => brl(v, 2) },
              { title: 'Vencimento', dataIndex: 'vencimento', render: v => dataBr(v) },
              { title: 'Status', dataIndex: 'status', render: (v, p) => <Tag>{p.nota_fiscal_numero ? `NF ${p.nota_fiscal_numero}` : v}</Tag> },
              { title: 'No controle', dataIndex: 'item_id', render: v => (v ? <Tag color="green">já está</Tag> : <Tag>novo</Tag>) },
            ]} />
        )}
      </Flex>
    </Modal>
  );
}

function ModalAbertura({ linha, inicio, onFechar, onSalvo }: { linha: LinhaContrato; inicio: string; onFechar: () => void; onSalvo: () => void }) {
  const { message } = AntApp.useApp();
  const [valor, setValor] = useState<number | null>(null);
  async function salvar() {
    try { await api.salvarAbertura(linha.contrato_id, valor ?? 0); message.success('Acumulado de abertura salvo.'); onSalvo(); onFechar(); }
    catch (e) { message.error(api.erroDe(e)); }
  }
  return (
    <Modal open title={`Acumulado de abertura — ${linha.numero}`} onCancel={onFechar} onOk={salvar} okText="Salvar" destroyOnHidden {...MODAL}>
      <Flex vertical gap={10}>
        <Text>Quanto deste contrato já tinha sido entregue antes de {inicio ? dataBr(inicio) : 'o início do controle'} (coluna “Entregue acumulado” da planilha). Entra no acumulado e no saldo, nunca no realizado de um mês.</Text>
        <InputNumber style={{ width: 220 }} min={0} decimalSeparator="," placeholder="R$" value={valor ?? undefined} onChange={v => setValor(v)} />
        <Text type="secondary">Salvar substitui o acumulado anterior (o anterior fica estornado no histórico). Zero apaga.</Text>
      </Flex>
    </Modal>
  );
}
